import { inject, Injectable } from '@angular/core';

import { EMPTY, from, interval, merge, Observable, of } from 'rxjs';
import {
    concatMap,
    distinctUntilChanged,
    finalize,
    map,
    mergeMap,
    share,
    startWith,
    tap,
} from 'rxjs/operators';
import { UploadStatus } from '@dxp/core/models';
import { ContentApiService } from '@dxp/core/api';

export interface IOverallUploadEvent {
    type: 'overall';
    progress: number;
    etaSeconds: number | null;
}

export interface ITaggedUploadEvent {
    id: string;
    file: File;
    status: UploadStatus;
}

export type UploadBusEvent = ITaggedUploadEvent | IOverallUploadEvent;

@Injectable({ providedIn: 'root' })
export class UploadService {
    readonly #contentApiService = inject(ContentApiService);

    splitDirAndFile(pathOrUrl: string): {
        directory: string;
        filename: string;
    } {
        const parts = pathOrUrl.split('/');

        if (parts.length === 2) {
            return {
                directory: parts[0],
                filename: parts[1],
            };
        } else {
            return { directory: '', filename: '' };
        }
    }

    uploadManyJodit(
        files: File[],
        makeId: (file: File, index: number) => string,
        concurrency = Math.max(1, files.length),
    ): Observable<ITaggedUploadEvent> {
        return from(files).pipe(
            mergeMap(
                (file, index) =>
                    this.#contentApiService.uploadJoditFile(file).pipe(
                        map(status => ({
                            id: makeId(file, index),
                            file,
                            status,
                        })),
                    ),
                concurrency,
            ),
        );
    }

    uploadManyJoditSequential(
        files: File[],
        makeId: (file: File, index: number) => string,
    ): Observable<UploadBusEvent> {
        if (!files.length) return EMPTY;

        const WINDOW_SEC = 8;
        const HEARTBEAT_MS = 500;
        const EWMA_TAU_SEC = 6;
        const STALE_SEC = 2.0;
        const WARMUP_SEC = 1.5;
        const MIN_BYTES_BEFORE_ETA = 256e3;

        const totalBytesAll = files.reduce((a, f) => a + (f.size ?? 0), 0);

        const lastLoadedById = new Map<string, number>();
        const windowPoints: { t: number; sum: number }[] = [];
        let overallStartTs: number | null = null;
        let ewmaRate: number | null = null;

        const tag =
            (file: File, index: number) =>
            (status: UploadStatus): ITaggedUploadEvent => ({
                id: makeId(file, index),
                file,
                status,
            });

        const computeOverall = (now: number): IOverallUploadEvent => {
            const sumLoaded = Array.from(lastLoadedById.values()).reduce(
                (a, b) => a + b,
                0,
            );
            const progress =
                totalBytesAll > 0
                    ? Math.max(
                          0,
                          Math.min(
                              100,
                              Math.round((sumLoaded * 100) / totalBytesAll),
                          ),
                      )
                    : 0;

            if (overallStartTs == null && sumLoaded > 0) {
                overallStartTs = now;
            }

            windowPoints.push({ t: now, sum: sumLoaded });

            const cutoff = now - WINDOW_SEC * 1000;
            while (windowPoints.length > 1 && windowPoints[0].t < cutoff) {
                windowPoints.shift();
            }

            let windowRate = 0;
            if (windowPoints.length >= 2) {
                const first = windowPoints[0]!;
                const last = windowPoints[windowPoints.length - 1]!;
                const dtSec = (last.t - first.t) / 1000;
                const dBytes = Math.max(0, last.sum - first.sum);
                if (dtSec > 0) windowRate = dBytes / dtSec;
            }

            if (windowPoints.length >= 2) {
                const last = windowPoints[windowPoints.length - 1]!;
                const prev = windowPoints[windowPoints.length - 2]!;
                const dtSec = Math.max(0, (last.t - prev.t) / 1000);
                if (dtSec > 0) {
                    const alpha = 1 - Math.exp(-dtSec / EWMA_TAU_SEC);
                    const inst = Math.max(0, (last.sum - prev.sum) / dtSec);
                    ewmaRate =
                        ewmaRate == null
                            ? inst
                            : ewmaRate + alpha * (inst - ewmaRate);
                }
            }

            const elapsedSec = overallStartTs
                ? (now - overallStartTs) / 1000
                : 0;
            const hasWarmup =
                elapsedSec >= WARMUP_SEC || sumLoaded >= MIN_BYTES_BEFORE_ETA;

            const longTermRate = sumLoaded / elapsedSec;

            const lastPoint = windowPoints[windowPoints.length - 1];
            const prevPoint =
                windowPoints.length > 1
                    ? windowPoints[windowPoints.length - 2]
                    : null;
            const lastDeltaSec = prevPoint
                ? (lastPoint.t - prevPoint.t) / 1000
                : Number.POSITIVE_INFINITY;
            const hasRecentProgress =
                prevPoint &&
                lastPoint.sum > prevPoint.sum &&
                lastDeltaSec <= STALE_SEC;

            const blendedRate = (() => {
                const candidates = [
                    hasRecentProgress ? windowRate : 0,
                    ewmaRate ?? 0,
                    longTermRate,
                ].filter(r => r > 0);

                if (candidates.length === 0) return 0;

                const wWindow = hasRecentProgress ? 0.5 : 0.0;
                const wEwma = ewmaRate ? 0.3 : 0.0;
                const wLt = 1 - (wWindow + wEwma);
                return (
                    wWindow * windowRate +
                    wEwma * (ewmaRate ?? 0) +
                    wLt * longTermRate
                );
            })();

            let etaSeconds: number | null = null;
            if (hasWarmup && blendedRate > 0 && sumLoaded < totalBytesAll) {
                etaSeconds = Math.ceil(
                    (totalBytesAll - sumLoaded) / blendedRate,
                );
                if (etaSeconds < 1) etaSeconds = 1;
            } else if (sumLoaded >= totalBytesAll) {
                etaSeconds = 0;
            } else {
                etaSeconds = null;
            }

            return { type: 'overall', progress, etaSeconds };
        };

        const perFile$ = from(files).pipe(
            concatMap((file, index) =>
                this.#contentApiService.uploadJoditFile(file).pipe(
                    startWith<UploadStatus>({
                        type: 'progress',
                        progress: 0,
                        etaSeconds: null,
                        loadedBytes: 0,
                        totalBytes: file.size ?? 0,
                    } as UploadStatus),
                    map(tag(file, index)),
                    tap(evt => {
                        if (evt.status.type === 'progress') {
                            const loaded = Math.min(
                                evt.status.loadedBytes ?? 0,
                                evt.file.size ?? Number.MAX_SAFE_INTEGER,
                            );
                            lastLoadedById.set(evt.id, loaded);
                        } else if (evt.status.type === 'success') {
                            lastLoadedById.set(evt.id, evt.file.size ?? 0);
                        }
                    }),
                ),
            ),
            share(),
        );

        const heartbeat$ = interval(HEARTBEAT_MS).pipe(startWith(0));

        const overall$ = merge(
            perFile$.pipe(map(() => performance.now())),
            heartbeat$.pipe(map(() => performance.now())),
        ).pipe(
            map(now => computeOverall(now)),
            distinctUntilChanged(
                (a, b) =>
                    a.progress === b.progress && a.etaSeconds === b.etaSeconds,
            ),
        );

        const initialOverall$ = of<IOverallUploadEvent>({
            type: 'overall',
            progress: 0,
            etaSeconds: null,
        });

        return merge(initialOverall$, overall$, perFile$).pipe(
            finalize(() => {
                lastLoadedById.clear();
                windowPoints.length = 0;
                ewmaRate = null;
                overallStartTs = null;
            }),
        );
    }

    removeJoditFile(directory: string, filename: string) {
        return this.#contentApiService.deleteFile(directory, filename);
    }
}
